import { useEffect, useState } from "react";
import { Plus, Trash2, Edit3, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const PRIORITY_COLORS = { Low: "#6C757D", Medium: "#457B9D", High: "#E76F51" };
const STATUS_COLORS = {
  "Not Started": "#6C757D",
  "In Progress": "#E85D04",
  Done: "#2A9D8F",
  Overdue: "#E76F51",
};

const blank = () => ({
  task_name: "",
  related_company: "",
  due_date: todayISO(),
  priority: "Medium",
  status: "Not Started",
  notes: "",
});

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const [t, m] = await Promise.all([api.listTasks(), api.getMeta()]);
    setTasks(t);
    setMeta(m);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...blank(), ...t }); setOpen(true); };

  const save = async () => {
    if (!form.task_name) return toast.error("Task name is required");
    try {
      if (editing) { await api.updateTask(editing.id, form); toast.success("Task updated"); }
      else { await api.createTask(form); toast.success("Task created"); }
      setOpen(false);
      load();
    } catch { toast.error("Failed to save task"); }
  };

  const cycle = async (t) => {
    const order = ["Not Started", "In Progress", "Done"];
    const idx = order.indexOf(t.status);
    const next = order[(idx + 1) % order.length];
    await api.updateTask(t.id, { ...t, status: next });
    load();
  };

  const del = async (t) => {
    if (!window.confirm(`Delete task "${t.task_name}"?`)) return;
    await api.deleteTask(t.id);
    toast.success("Task deleted");
    load();
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div data-testid="page-tasks">
      <PageHeader
        overline="To Do"
        title="Tasks"
        subtitle="Calls, deliveries, follow-ups — keep the pipeline moving."
        actions={
          <div className="flex gap-2">
            <ExportButton module="tasks" />
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-task-btn">
              <Plus size={16} className="mr-2" /> New Task
            </Button>
          </div>
        }
      />

      {tasks.length === 0 ? (
        <EmptyState icon={ListChecks} title="No tasks yet" description="Add tasks like Call customer, Deliver samples, Send price list…" />
      ) : (
        <div className="card-soft divide-y divide-[#E5E5E5]">
          {tasks.map((t) => (
            <div
              key={t.id}
              className="flex items-center gap-3 px-4 sm:px-5 py-4 hover:bg-[#F7F1E8]/30"
              data-testid={`task-row-${t.id}`}
            >
              <button
                onClick={() => cycle(t)}
                className="w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                style={{
                  borderColor: STATUS_COLORS[t.status],
                  backgroundColor: t.status === "Done" ? STATUS_COLORS["Done"] : "transparent",
                }}
                data-testid={`task-toggle-${t.id}`}
                aria-label="cycle status"
              >
                {t.status === "Done" && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold truncate ${t.status === "Done" ? "line-through text-[#6C757D]" : "text-[#222]"}`}>
                  {t.task_name}
                </div>
                <div className="text-xs text-[#6C757D] truncate">
                  {t.related_company || "—"} · Due {formatDateEU(t.due_date)}
                </div>
              </div>
              <span
                className="hidden sm:inline-flex text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: `${PRIORITY_COLORS[t.priority]}1f`, color: PRIORITY_COLORS[t.priority] }}
              >
                {t.priority}
              </span>
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                style={{ backgroundColor: `${STATUS_COLORS[t.status]}1f`, color: STATUS_COLORS[t.status] }}
              >
                {t.status}
              </span>
              <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}>
                <Edit3 size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => del(t)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-task-${t.id}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Task" : "New Task"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Task Name *" className="sm:col-span-2">
              <Input value={form.task_name} onChange={(e) => setForm({ ...form, task_name: e.target.value })}
                placeholder="e.g. Call Maria for feedback"
                data-testid="input-task-name" />
            </Field>
            <Field label="Related Company">
              <Input value={form.related_company} onChange={(e) => setForm({ ...form, related_company: e.target.value })}
                data-testid="input-task-company" />
            </Field>
            <Field label="Due Date">
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="input-task-due" />
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="input-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.task_priorities.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="input-task-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.task_statuses.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes" className="sm:col-span-2">
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-task">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-task">
              {editing ? "Save Changes" : "Create Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
